// Tiny PostHog wrapper — no-ops if PostHog isn't loaded.
export const track = (event: string, props: Record<string, unknown> = {}) => {
  if (typeof window === "undefined") return;
  const ph = (window as any).posthog;
  if (ph && typeof ph.capture === "function") {
    try {
      ph.capture(event, props);
    } catch (err) {
      // Swallow — analytics must never break the UI.
      console.warn("[analytics] capture failed", err);
    }
  }
};

// GTM / Meta Pixel bridge — pushes browser events into dataLayer.
// In debug mode (URL has ?pixel_debug=1), also logs and shows a toast so
// you can verify the push without opening DevTools.
export const pushDataLayerEvent = (event: string, props: Record<string, unknown> = {}) => {
  if (typeof window === "undefined") return;

  try {
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({ event, ...props });

    const debug =
      new URLSearchParams(window.location.search).get("pixel_debug") === "1" ||
      sessionStorage.getItem("pixel_debug") === "1";
    if (debug) {
      sessionStorage.setItem("pixel_debug", "1"); // sticky across navigations
      // eslint-disable-next-line no-console
      console.log("[pixel_debug] dataLayer push:", { event, ...props });
      // Lazy-load sonner so this stays a no-cost no-op for normal users.
      import("sonner")
        .then(({ toast }) => {
          toast.success(`Pixel event fired: ${event}`, {
            description: JSON.stringify(props),
            duration: 6000,
          });
        })
        .catch(() => {});
    }
  } catch (err) {
    // Swallow — tracking must never block the funnel.
    console.warn("[analytics] dataLayer push failed", err);
  }
};

// Direct Meta Pixel call — bypasses GTM trigger config. Requires the base
// Pixel script (loaded via GTM today) to have defined window.fbq.
// In debug mode (?pixel_debug=1), logs + toasts so you can verify firing.
export const fbqTrack = (event: string, props: Record<string, unknown> = {}) => {
  if (typeof window === "undefined") return;

  const debug =
    new URLSearchParams(window.location.search).get("pixel_debug") === "1" ||
    sessionStorage.getItem("pixel_debug") === "1";

  try {
    const fbq = (window as any).fbq;
    if (typeof fbq === "function") {
      fbq("track", event, props);
      if (debug) {
        sessionStorage.setItem("pixel_debug", "1");
        // eslint-disable-next-line no-console
        console.log("[pixel_debug] fbq track:", { event, ...props });
        import("sonner")
          .then(({ toast }) =>
            toast.success(`Meta Pixel fired: ${event}`, {
              description: JSON.stringify(props),
              duration: 6000,
            }),
          )
          .catch(() => {});
      }
    } else if (debug) {
      // eslint-disable-next-line no-console
      console.warn("[pixel_debug] fbq not loaded — Meta Pixel base script missing");
      import("sonner")
        .then(({ toast }) =>
          toast.error(`fbq not loaded — base Pixel missing`, {
            description: `Tried to fire ${event}`,
            duration: 6000,
          }),
        )
        .catch(() => {});
    }
  } catch (err) {
    console.warn("[analytics] fbq track failed", err);
  }
};
